import { CreditCardIcon } from '@heroicons/react/24/outline'
import { Chip } from '@heroui/react'
import { Badge, Button, Skeleton } from '@radix-ui/themes'
import { useAuth } from 'hooks/useAuth'
import {
  BadgeCheck,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { $fetch } from 'utils/fetch'
import { ReturnMap } from 'utils/tools/mapRateBilling'
import { getOrgId } from '../../../utils/funcs/browser/getOrgId'
import AutoRecharge from './AutoRecharge'
import CreditsNotification from './CreditsNotification'
import EditBillingInfoModal from './EditBillingInfoModal'

function BillingAndPayments({ embedded = false }) {
  const orgId = getOrgId()
  const { org, credits } = useAuth()
  const [hasPaymentMethod, setHasPaymentMethod] = useState(false)
  const [stripeUrl, setStripeUrl] = useState()
  const [loading, setLoading] = useState(false)
  const [stripeCustomer, setStripeCustomer] = useState(null)

  const fetchStripeUrl = async () => {
    const headers = {
      ...(orgId && { 'x-bland-org-id': orgId }),
    }
    await $fetch('/pay/createDetails', {
      method: 'POST',
      headers,
    })
    const res = await $fetch(`/orgs/${orgId}/billing/session`)
    setStripeUrl(res.data?.redirect_url)
    sessionStorage.setItem('billing_redirect_url', res.data?.redirect_url)
  }

  const fetchStripeDetails = async () => {
    setLoading(true)
    const res = await $fetch('/billing/customer')

    if (res.data) {
      setHasPaymentMethod(res.data.has_payment_method)
      if (res?.data?.customer) {
        setStripeCustomer(res?.data?.customer)
      }
    }
    setLoading(false)
  }

  const openStripePortal = async () => {
    if (!stripeUrl) {
      toast.error('You don\'t have permission to edit this payment method')
    }
    else {
      window.open(stripeUrl, '_blank')
    }
  }

  useEffect(() => {
    // To determine if there is a payment method on file
    fetchStripeDetails()
    // To determine if user has permissions
    fetchStripeUrl()
  }, [])

  return (
    <div>
      <div className="border-[1.5px] bg-white rounded-lg border-color:#f0f0f0 shadow-sm">
        <div className="border-b border-color:#f0f0f0 p-4 py-5">
          <div className="flex justify-between items-center">
            <span className="text-black font-semibold text-base">
              Billing Details
            </span>
            <div className="flex items-center gap-2.5">
              <Button
                variant="outline"
                color="gray"
                radius="large"
                type="button"
                onClick={openStripePortal}
              >
                <span className="text-black">Manage Billing</span>
              </Button>
            </div>
          </div>
        </div>
        <div className="border-b border-color:#f0f0f0 p-4">
          <div className="flex items-center justify-between">
            <span className="text-black font-medium text-md">Current Plan</span>
            <div className="">
              <Badge size="2" radius="full">
                {ReturnMap(org.org_rate_limit)?.description}
              </Badge>
              <p></p>
            </div>
          </div>

          <div className="flex items-start justify-between mt-5">
            <span className="text-black font-normal text-xs">Rate Limit</span>
            <div className="flex flex-col gap-2">
              <p className="text-xs font-normal text-right">
                {ReturnMap(org.org_rate_limit)?.minute}
                {' '}
                calls / minute
              </p>
              <p className="text-xs font-normal text-right">
                {ReturnMap(org.org_rate_limit)?.hourly}
                {' '}
                calls / hourly
              </p>
              <p className="text-xs font-normal text-right">
                {ReturnMap(org.org_rate_limit)?.daily}
                {' '}
                calls / daily
              </p>
            </div>
          </div>
        </div>
        <div className="border-b border-color:#f0f0f0 p-4">
          <div className="flex items-center justify-between">
            <span className="text-black font-medium text-md">
              Available Credits
            </span>
            <span className="text-black font-normal text-xs">
              {credits?.toFixed(2) || 'Not available'}
            </span>
          </div>
        </div>
        <div className="border-b border-color:#f0f0f0 p-4">
          <div className="flex items-center justify-between">
            <span className="text-black font-medium text-md">
              Payment Method
            </span>
            <div>
              {hasPaymentMethod
                ? (
                    <Chip
                      size="sm"
                      color="success"
                      classNames={{
                        content: 'font-bold',
                      }}
                      variant="faded"
                      startContent={<BadgeCheck size={16} className="fill-green-500 text-gray-50" />}
                    >
                      Payment Method on File
                    </Chip>
                  )
                : loading
                  ? (
                      <Skeleton className="w-48 h-6" />
                    )
                  : (
                      <button
                        onClick={openStripePortal}
                        className="inline-flex items-center px-2 py-1.5 border border-transparent text-xs leading-2.5 font-medium rounded text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                      >
                        <CreditCardIcon className="h-2.5 w-2.5 mr-1.5" />
                        Add Payment Method
                      </button>
                    )}
            </div>
          </div>
        </div>
        <div className="p-4 flex flex-col gap-4">
          <div className="flex items-center w-full">
            <span className="text-black font-medium text-md">
              Billing Information
            </span>
            <p className="text-gray-500 text-xs ml-4 mr-auto">
              This information will be displayed on your invoices.
            </p>
            <EditBillingInfoModal stripeCustomer={stripeCustomer} onSuccess={fetchStripeDetails} />
          </div>

          {stripeCustomer?.name
            && (
              <span className="flex justify-between">
                <p className="text-black font-medium text-md">Name</p>
                <p className="text-black font-normal text-md">{stripeCustomer?.name ?? 'Not set'}</p>
              </span>
            )}
          {stripeCustomer?.email && (
            <span className="flex justify-between">
              <p className="text-black font-medium text-md">Email</p>
              <p>{stripeCustomer?.email}</p>
            </span>
          )}
          {stripeCustomer?.phone && (
            <span className="flex justify-between">
              <p className="text-black font-medium text-md">Phone</p>
              <p>{stripeCustomer?.phone}</p>
            </span>
          )}
          {stripeCustomer?.address?.line1 && (
            <span className="flex justify-between">
              <p className="text-black font-medium text-md">Address</p>
              <span className="flex flex-col items-end gap-1">
                <p>{stripeCustomer?.address?.line1}</p>
                {stripeCustomer?.address?.line2 && (
                  <p>{stripeCustomer?.address?.line2}</p>
                )}
                <p>{stripeCustomer?.address?.city}</p>
                <p>{stripeCustomer?.address?.state}</p>
                <p>{stripeCustomer?.address?.postal_code}</p>
                <p>{stripeCustomer?.address?.country}</p>
              </span>
            </span>
          )}
          {stripeCustomer?.tax_id?.value && (
            <span className="flex justify-between">
              <p className="text-black font-medium text-md">Tax ID</p>
              <p>{stripeCustomer?.tax_id?.value ?? 'Not set'}</p>
            </span>
          )}
        </div>

      </div>

      {!embedded && (
        <div className="border-[1.5px] bg-white rounded-lg border-color:#f0f0f0 shadow-sm p-4 mt-8">
          <AutoRecharge />
        </div>
      )}

      {!embedded && (
        <div className="border-[1.5px] bg-white rounded-lg border-color:#f0f0f0 shadow-sm p-4 mt-8">
          <CreditsNotification />
        </div>
      )}
    </div>
  )
}

export default BillingAndPayments
